import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, PageHeader, Modal, Input, Select } from '../../components/ui';
import api from '../../services/api';
import toast from 'react-hot-toast';

const CATEGORIES = ['Cold Outreach','Follow Up','Newsletter','Promotional','Re-engagement','Welcome','Partnership','Product Demo','Event Invite','Thank You'];

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ name: '', subject: '', body: '', category: 'Cold Outreach' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try { const r = await api.get('/templates'); setTemplates(r.data.data || []); }
    catch {} finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm({ name:'', subject:'', body:'', category:'Cold Outreach' }); setShowModal(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, subject: t.subject, body: t.body, category: t.category }); setShowModal(true); };

  const save = async () => {
    if (!form.name || !form.body) return toast.error('Name and body required');
    setSaving(true);
    try {
      if (editing) { await api.put(`/templates/${editing.id}`, form); toast.success('Template updated!'); }
      else { await api.post('/templates', form); toast.success('Template saved!'); }
      setShowModal(false); fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api.delete(`/templates/${id}`); toast.success('Deleted'); fetchTemplates(); } catch { toast.error('Failed'); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div>
      <PageHeader title="Templates" subtitle="Save and reuse email templates" action={<Button onClick={openCreate}>+ New Template</Button>} />

      {loading ? <div style={{ color:'#555', textAlign:'center', padding:40 }}>Loading...</div> : (
        templates.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📝</div>
            <h3 style={{ color: '#ccc', marginBottom: 8 }}>No templates yet</h3>
            <p style={{ color: '#555', fontSize: 14, marginBottom: 24 }}>Create reusable email templates for your campaigns.</p>
            <Button onClick={openCreate}>+ Create Template</Button>
          </Card>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))', gap: 16 }}>
            {templates.map(t => (
              <Card key={t.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{t.name}</div>
                    <Badge color="info">{t.category || 'General'}</Badge>
                  </div>
                  <div style={{ fontSize: 11, color: '#555' }}>Used {t.usedCount || 0}×</div>
                </div>
                {t.subject && <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Subject: {t.subject}</div>}
                <div style={{ fontSize: 12, color: '#555', marginBottom: 14, lineHeight: 1.6, overflow: 'hidden', maxHeight: 48 }}>
                  {t.body?.replace(/<[^>]+>/g, '').substring(0, 100)}...
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="sm" variant="ghost" onClick={() => setPreview(t)} style={{ flex: 1 }}>👁 Preview</Button>
                  <Button size="sm" variant="secondary" onClick={() => openEdit(t)} style={{ flex: 1 }}>✏️ Edit</Button>
                  <Button size="sm" variant="danger" onClick={() => deleteTemplate(t.id)}>🗑</Button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {/* Create/Edit Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Template' : 'Create Template'} size="lg">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label="Template Name *" placeholder="e.g. Cold Intro Email" value={form.name} onChange={e => f('name', e.target.value)} />
          <Select label="Category" value={form.category} onChange={e => f('category', e.target.value)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
        </div>
        <Input label="Subject Line" placeholder="e.g. Quick question about {{company}}" value={form.subject} onChange={e => f('subject', e.target.value)} />
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#ccc', marginBottom: 6 }}>Email Body *</label>
          {/* Toolbar */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
            {[
              { label: 'B', title: 'Bold', wrap: ['<b>', '</b>'] },
              { label: 'I', title: 'Italic', wrap: ['<i>', '</i>'] },
              { label: 'U', title: 'Underline', wrap: ['<u>', '</u>'] },
            ].map(({ label, title, wrap }) => (
              <button key={label} title={title} onMouseDown={e => {
                e.preventDefault();
                const ta = document.getElementById('tpl-body');
                const start = ta.selectionStart, end = ta.selectionEnd;
                const selected = form.body.slice(start, end);
                const newVal = form.body.slice(0, start) + wrap[0] + selected + wrap[1] + form.body.slice(end);
                f('body', newVal);
                setTimeout(() => { ta.focus(); ta.setSelectionRange(start + wrap[0].length, end + wrap[0].length); }, 0);
              }} style={{ padding: '4px 10px', background: '#222', border: '1px solid rgba(46,204,113,0.2)', borderRadius: 6, color: '#ccc', cursor: 'pointer', fontWeight: label === 'B' ? 700 : 400, fontStyle: label === 'I' ? 'italic' : 'normal', textDecoration: label === 'U' ? 'underline' : 'none', fontSize: 13 }}>{label}</button>
            ))}
            <button title="Insert Hyperlink" onMouseDown={e => {
              e.preventDefault();
              const ta = document.getElementById('tpl-body');
              const start = ta.selectionStart, end = ta.selectionEnd;
              const selected = form.body.slice(start, end) || 'click here';
              const url = window.prompt('Enter URL:', 'https://');
              if (!url) return;
              const link = `<a href="${url}">${selected}</a>`;
              const newVal = form.body.slice(0, start) + link + form.body.slice(end);
              f('body', newVal);
              setTimeout(() => { ta.focus(); }, 0);
            }} style={{ padding: '4px 10px', background: '#222', border: '1px solid rgba(46,204,113,0.2)', borderRadius: 6, color: '#2ECC71', cursor: 'pointer', fontSize: 13 }}>🔗 Link</button>
            <button title="Insert Button" onMouseDown={e => {
              e.preventDefault();
              const ta = document.getElementById('tpl-body');
              const start = ta.selectionStart;
              const url = window.prompt('Enter URL for button:', 'https://');
              if (!url) return;
              const label = window.prompt('Button text:', 'Click Here');
              if (!label) return;
              const btn = `<a href="${url}" style="display:inline-block;padding:10px 24px;background:#2ECC71;color:#fff;text-decoration:none;border-radius:6px;font-weight:bold;">${label}</a>`;
              const newVal = form.body.slice(0, start) + btn + form.body.slice(start);
              f('body', newVal);
              setTimeout(() => { ta.focus(); }, 0);
            }} style={{ padding: '4px 10px', background: '#222', border: '1px solid rgba(46,204,113,0.2)', borderRadius: 6, color: '#3498DB', cursor: 'pointer', fontSize: 13 }}>🔘 Button</button>
          </div>
          <textarea id="tpl-body" value={form.body} onChange={e => f('body', e.target.value)} rows={10} placeholder={'Hi {{first_name}},\n\nYour email body here...\n\nBest regards'}
            style={{ width: '100%', padding: '12px 14px', background: '#1a1a1a', border: '1px solid rgba(46,204,113,0.15)', borderRadius: 10, color: '#fff', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'monospace', lineHeight: 1.7 }} />
          <div style={{ fontSize: 11, color: '#555', marginTop: 4 }}>Variables: {'{{first_name}}'} {'{{last_name}}'} {'{{company}}'} {'{{email}}'} &nbsp;|&nbsp; Select text then click toolbar button to format</div>
          {form.body && form.body.includes('<') && (
            <div style={{ marginTop:10 }}>
              <div style={{ fontSize:11, color:'#555', marginBottom:4 }}>👁 Preview (how it looks in email):</div>
              <div style={{ background:'#fff', borderRadius:8, padding:'12px 16px', fontSize:14, lineHeight:1.8, color:'#222' }}
                dangerouslySetInnerHTML={{ __html: form.body.replace(/\n/g,'<br>') }} />
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => setShowModal(false)} style={{ flex: 1 }}>Cancel</Button>
          <Button loading={saving} onClick={save} style={{ flex: 2 }}>{editing ? 'Update Template' : 'Save Template'}</Button>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal open={!!preview} onClose={() => setPreview(null)} title={`Preview: ${preview?.name}`} size="lg">
        {preview && (
          <>
            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Subject:</div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{preview.subject || '(No subject)'}</div>
            </div>
            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: 16, minHeight: 200, fontSize: 14, color: '#ccc', lineHeight: 1.8, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {preview.body?.replace(/<[^>]+>/g, '')}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
